/**
 * #4155 — histórico mesclado por contato.
 *
 * Com `mergeHistoryByContact` ligado, o fio de um ticket mostra TODAS as
 * conversas do contato — inclusive atendimentos posteriores. Isso trouxe três
 * problemas que estas funções resolvem, todas puras para poderem ser testadas
 * sem montar o componente.
 */

/**
 * Decide se uma mensagem que chegou pelo socket pertence ao fio aberto.
 *
 * Sem o merge, só as do próprio ticket. Com o merge, também as de outros
 * tickets do MESMO contato — senão o fio ficaria parado enquanto um F5 já
 * mostraria a mensagem (o backend a inclui no histórico).
 */
export const belongsToThisThread = ({
  incomingTicket,
  openTicketUuid,
  openTicket,
  mergeHistoryByContact
}) => {
  if (!incomingTicket) return false;
  if (incomingTicket.uuid === openTicketUuid) return true;
  if (!mergeHistoryByContact) return false;
  if (!openTicket?.contactId) return false;

  return (
    incomingTicket.contactId === openTicket.contactId &&
    !!incomingTicket.isGroup === !!openTicket.isGroup
  );
};

/**
 * Mescla a mensagem que veio num evento `update` com a que já está na lista.
 *
 * O update de ACK (o ✓✓) reemite a Message com um ticket PARCIAL: tem id e
 * queueId, mas não os includes `queue` e `whatsapp`. Sobrescrever cegamente
 * apagava fila e conexão, e a faixa do histórico regredia para "Sem Fila"
 * logo depois de aparecer correta.
 */
export const mergeUpdatedMessage = (previous, incoming) => {
  const merged = { ...previous, ...incoming };

  if (previous?.ticket && incoming?.ticket) {
    merged.ticket = {
      ...previous.ticket,
      ...incoming.ticket,
      queue: incoming.ticket.queue ?? previous.ticket.queue,
      whatsapp: incoming.ticket.whatsapp ?? previous.ticket.whatsapp
    };
  } else if (previous?.ticket && !incoming?.ticket) {
    merged.ticket = previous.ticket;
  }

  return merged;
};

/**
 * Índice ticketId -> { queue, whatsapp } montado UMA vez por render da lista.
 *
 * A faixa separadora precisa de fila/conexão, mas a mensagem que a ancora pode
 * ter ficado com o ticket parcial (ver mergeUpdatedMessage). Buscar numa
 * mensagem irmã a cada faixa seria O(faixas x mensagens); com o índice o custo
 * é uma passada só, e a consulta da faixa vira O(1).
 */
export const buildTicketDataIndex = (messagesList) => {
  const index = new Map();
  if (!Array.isArray(messagesList)) return index;

  for (const message of messagesList) {
    const ticketId = message?.ticketId;
    if (ticketId == null) continue;

    const atual = index.get(ticketId);
    // já temos os dois campos deste ticket: nada a fazer
    if (atual?.queue && atual?.whatsapp) continue;

    const ticket = message?.ticket;
    if (!ticket) continue;

    index.set(ticketId, {
      queue: atual?.queue ?? ticket.queue,
      whatsapp: atual?.whatsapp ?? ticket.whatsapp
    });
  }

  return index;
};

/**
 * Resolve a fila que a faixa deve mostrar para uma seção do histórico.
 *
 * #4241 — a faixa lia a fila só das MENSAGENS, e mensagem é dado congelado: ao
 * transferir ou aceitar o ticket, nada na lista é recarregado, então a faixa
 * seguia mostrando a fila antiga (normalmente "Sem Fila") até o F5. O ticket
 * aberto, ao contrário, é atualizado ao vivo pelo socket — para a seção DELE,
 * essa é a fonte correta. As demais seções (histórico mesclado de outros
 * tickets do contato) continuam vindo da mensagem/índice.
 *
 * A decisão é guiada pelo `queueId` do ticket aberto, não pelo include `queue`:
 * um payload parcial (com queueId, sem o include) não pode ser lido como "sem
 * fila" — nesse caso vale o dado antigo, que ao menos não regride.
 */
export const resolveSeparatorQueue = ({
  sectionTicketId,
  openTicketId,
  openTicketQueue,
  openTicketQueueId,
  messageQueue,
  indexQueue
}) => {
  const anterior = messageQueue ?? indexQueue;

  if (openTicketId == null || sectionTicketId !== openTicketId) return anterior;

  // ticket aberto está comprovadamente sem fila (transferido para ninguém)
  if (openTicketQueueId == null) return null;

  // tem fila: usa o include quando veio; senão preserva o que já havia
  return openTicketQueue ?? anterior;
};

/**
 * Decide se a faixa de separação deve aparecer nesta posição da lista.
 *
 * A faixa marca troca de ticket. A PRIMEIRA seção (sem mensagem anterior) só
 * ganha faixa com o merge ligado: sem merge o fio começa sempre no ticket
 * aberto e a faixa seria redundante; com merge ele começa em um ticket
 * qualquer do contato e ficaria anônimo.
 */
export const shouldRenderSeparator = ({
  previousTicketId,
  currentTicketId,
  mergeHistoryByContact
}) => {
  if (previousTicketId === currentTicketId) return false;
  if (previousTicketId === undefined && !mergeHistoryByContact) return false;
  return true;
};
